import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The one way anything on this app expands in place: a muted chevron and a
 * short label. Pills mean status and text links navigate — neither toggles.
 * Everything the agent writes renders through `NoteDisclosure`, so prose is
 * always one click away and never on the page by default.
 */
export function DisclosureToggle({ open, onToggle, className, children }: {
  open: boolean;
  onToggle: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={cn(
        'inline-flex items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground',
        className
      )}
    >
      {open ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
      <span className="inline-flex flex-wrap items-center gap-x-1.5">{children}</span>
    </button>
  );
}

export function Disclosure({ label, count, defaultOpen = false, className, contentClassName, children }: {
  label: ReactNode;
  count?: number;
  defaultOpen?: boolean;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={className}>
      <DisclosureToggle open={open} onToggle={() => setOpen((v) => !v)}>
        {label}
        {count != null && <span className="tabular-nums">{count}</span>}
      </DisclosureToggle>
      {open && <div className={cn('mt-1.5', contentClassName)}>{children}</div>}
    </div>
  );
}

/** Agent prose, on request: the text verbatim, behind a "Note" toggle. */
export function NoteDisclosure({ text, label = 'Note', className }: {
  text: string | null | undefined;
  label?: ReactNode;
  className?: string;
}) {
  if (!text) return null;
  return (
    <Disclosure label={label} className={className}>
      <div className="whitespace-pre-wrap rounded-md bg-muted/30 px-2.5 py-1.5 text-xs text-muted-foreground">
        {text}
      </div>
    </Disclosure>
  );
}
